import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { useSubscription } from "../../hooks/useSubscription";
import { UNIFIED_MENU, userHasCompanyRole } from "../../config/subscriptionConfig";
import { normalizeSidebarPreferences, isUserMenuItem } from "../../utils/sidebarPreferences";
import {
  LayoutDashboard,
  Award,
  Users,
  Send,
  FileText,
  GraduationCap,
  Bell,
  User,
  Settings,
  LogOut,
  AlarmClock,
  HardHat,
  Package,
  Truck,
  Clock,
  Calendar,
  Shield,
  ClipboardList,
  ClipboardCheck,
  FolderKanban,
  ChevronDown,
  Phone,
  CreditCard,
  ArrowLeftRight,
  MessageCircle,
  CalendarCheck,
  RefreshCw,
  Car,
  Plane,
} from "lucide-react";
import logoSygnet from "../../assets/V1_sygnet.png";
import api from "../../services/apiService";
import AccountSwitcher from './AccountSwitcher';

// Mapowanie nazw ikon na komponenty
const ICON_MAP = {
  LayoutDashboard,
  Award,
  Users,
  Send,
  FileText,
  GraduationCap,
  AlarmClock,
  HardHat,
  Package,
  Truck,
  Clock,
  Calendar,
  Shield,
  ClipboardList,
  ClipboardCheck,
  Bell,
  FolderKanban,
  CalendarCheck,
  MessageCircle,
  Car,
  Plane,
};

const UnifiedSidebar = ({ onOpenPhoneBook, viewMode, setViewMode }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Always a 64px rail. It widens OVER the page — never pushing it — while the pointer is on it,
  // after a click on it (touch screens have no hover), or while its user menu is open. Nothing
  // pins it open: leaving it, clicking elsewhere or going to another page folds it again, and the
  // arrow folds it right away (it stays folded until the pointer leaves and comes back).
  const [isHovering, setIsHovering] = useState(false);
  const [openedByClick, setOpenedByClick] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sidebarRef = useRef(null);
  const [openMenus, setOpenMenus] = useState({});
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [openIssueCount, setOpenIssueCount] = useState(0);
  const [companyInfo, setCompanyInfo] = useState(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(UserContext);

  const { loading: subscriptionLoading, filterMenu, hasSubscription, hasAnySubscription } = useSubscription();

  // Sprawdź czy user jest adminem (może przełączać widok)
  const isAdmin = ['admin', 'adminves', 'superuser'].includes(user?.role);

  // Filter menu based on subscriptions and user role
  const baseMenuItems = filterMenu(UNIFIED_MENU).filter(item => {
    if (item.requireCompany && !user?.company) return false;
    if (item.requireTrainingRoles && (!user?.training_roles || user.training_roles.length === 0)) return false;
    // Car ordering is opt-in per company (Company.car_order_enabled). The same
    // flag already hides the fleet queue in the Cars submenu; a top-level item
    // needs it here, because the submenu filter below never sees one.
    if (item.carOrdersOnly && !user?.carOrderEnabled) return false;
    // Time & Attendance is opt-in per company (Company.worktime_enabled).
    if (item.worktimeOnly && !user?.worktimeEnabled) return false;
    // Leave approvals: only for people who decide somebody's leave (approval groups, T&A).
    if (item.leaveApproverOnly && user?.approvesLeave !== true) return false;
    return true;
  });

  // Czy admin ma ograniczenia z company_role (menuPermissions)
  // Rola z pustą listą też zawęża (do niczego) — tylko brak roli znaczy „bez filtra".
  const hasMenuPermissions = userHasCompanyRole(user);

  // Dodatkowe filtrowanie dla adminów w zależności od viewMode
  const visibleMenuItems = isAdmin
    ? baseMenuItems.filter(item => {
        if (item.type === 'separator') return false;
        if (!item.roles || item.roles.length === 0) return true;

        if (viewMode === 'admin') {
          // W trybie admin ukryj pozycje "my-*" (typowo userowe)
          if (item.id.startsWith('my-')) return false;
          // Pokazuj tylko pozycje które mają role admina
          const isAdminItem = item.roles.some(r => ['admin', 'adminves', 'superuser'].includes(r));
          if (!isAdminItem) return false;
          // Admin z company_role — filtruj po menuPermissions (respektuje permissionId)
          const permKey = item.permissionId || item.id;
          if (hasMenuPermissions && !(user.menuPermissions || []).includes(permKey)) return false;
          return true;
        } else {
          // W trybie user pokazuj tylko pozycje "my-*" lub te które mają role usera ale nie są typowo adminowe
          const hasUserRole = item.roles.some(r => ['user', 'userves'].includes(r));
          const isUserItem = item.id.startsWith('my-');
          return hasUserRole || isUserItem;
        }
      })
    : baseMenuItems;

  // Filter submenu items based on company role menuPermissions (for users with roles — including admins)
  const roleFilteredItems = hasMenuPermissions
    ? visibleMenuItems.map(item => {
        if (!item.submenu || item.submenu.length === 0) return item;
        const filteredSubmenu = item.submenu.filter(sub =>
          (user.menuPermissions || []).includes(sub.id)
        );
        // If no submenu items are permitted, show all (category-level permission implies all subcategories)
        if (filteredSubmenu.length === 0) return item;
        return { ...item, submenu: filteredSubmenu };
      })
    : visibleMenuItems;

  // 3rd layer: User's own sidebar preferences (hidden items + custom order)
  // Normalized to { user: { hidden, order, subOrder }, admin: { hidden, order, subOrder } }
  const normalizedPrefs = normalizeSidebarPreferences(user?.sidebarPreferences);

  // Helper: apply a single preferences section to a list of items
  const applyPrefsSection = (items, prefs) => {
    const { hidden = [], order = [], subOrder = {} } = prefs;

    // Filter out hidden categories and subcategories
    let result = hidden.length > 0
      ? items
          .filter(item => !hidden.includes(item.id))
          .map(item => {
            if (!item.submenu || item.submenu.length === 0) return item;
            const filteredSubmenu = item.submenu.filter(sub => !hidden.includes(sub.id));
            return { ...item, submenu: filteredSubmenu };
          })
      : items;

    // Reorder categories based on custom order
    if (order.length > 0) {
      result = [...result].sort((a, b) => {
        const indexA = order.indexOf(a.id);
        const indexB = order.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
    }

    // Reorder subcategories within each category
    if (Object.keys(subOrder).length > 0) {
      result = result.map(item => {
        if (!item.submenu || item.submenu.length === 0) return item;
        const subOrd = subOrder[item.id];
        if (!subOrd || subOrd.length === 0) return item;
        const sortedSubmenu = [...item.submenu].sort((a, b) => {
          const iA = subOrd.indexOf(a.id);
          const iB = subOrd.indexOf(b.id);
          if (iA !== -1 && iB !== -1) return iA - iB;
          if (iA !== -1) return -1;
          if (iB !== -1) return 1;
          return 0;
        });
        return { ...item, submenu: sortedSubmenu };
      });
    }

    return result;
  };

  const finalMenuItems = (() => {
    const isCoordinatorOrInspector = !isAdmin && (user?.isCoordinator || user?.inspector);

    if (isCoordinatorOrInspector) {
      // Coordinator/Inspector: split items into user and admin sections,
      // apply separate preferences to each, then recombine with separator
      const userItems = roleFilteredItems.filter(item =>
        item.type !== 'separator' && isUserMenuItem(item.id)
      );
      const separator = roleFilteredItems.find(item => item.type === 'separator');
      const adminItems = roleFilteredItems.filter(item =>
        item.type !== 'separator' && !isUserMenuItem(item.id)
      );

      const orderedUser = applyPrefsSection(userItems, normalizedPrefs.user);
      const orderedAdmin = applyPrefsSection(adminItems, normalizedPrefs.admin);

      return [
        ...orderedUser,
        ...(separator && orderedAdmin.length > 0 ? [separator] : []),
        ...orderedAdmin,
      ];
    }

    if (isAdmin) {
      // Admin: use the section matching current viewMode
      const prefs = viewMode === 'admin' ? normalizedPrefs.admin : normalizedPrefs.user;
      return applyPrefsSection(roleFilteredItems, prefs);
    }

    // Regular user: use the user section
    return applyPrefsSection(roleFilteredItems, normalizedPrefs.user);
  })();

  // Load ticket notifications
  useEffect(() => {
    const loadTicketNotifications = async () => {
      if (!user?.id) return;
      try {
        const response = await api.get('/support/tickets/unread_notifications/');
        setTicketNotifications(response.data.notifications || []);
        setNotificationCount(response.data.count || 0);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };
    loadTicketNotifications();
    const interval = setInterval(loadTicketNotifications, 60000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Load open issue count
  useEffect(() => {
    const loadIssueCount = async () => {
      if (!user?.id) return;
      try {
        const response = await api.get('/vehicle-issues/');
        const all = response.data.results || response.data || [];
        setOpenIssueCount(all.filter(i => i.status !== 'resolved').length);
      } catch {}
    };
    loadIssueCount();
    const interval = setInterval(loadIssueCount, 120000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Load company info (logo + email) so the user always sees which company they're in
  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!user?.id || !user?.company) return;
      try {
        const response = await api.get('/company/settings/');
        setCompanyInfo(response.data || null);
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyInfo();
  }, [user?.id, user?.company]);

  // A new page folds a sidebar that was opened by a click.
  useEffect(() => { setOpenedByClick(false); }, [location.pathname]);

  // Handle clicks outside menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setOpenedByClick(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      if (logout) logout();
      navigate("/login", { replace: true });
    }
  };

  // How well a category claims the current URL. Two categories can share the first path segment —
  // Travel lives at /daily-task-coordination/travel-coordination, next to Daily Task Coordination —
  // so a category whose own path or one of its pages is a prefix of the URL beats one that merely
  // shares the segment, and the longer prefix wins. 0 = not this category.
  const activeScore = (item) => {
    if (!item?.path) return 0;
    const current = location.pathname;
    const prefixes = [item.path, ...(item.submenu || []).map((sub) => sub.path)].filter(Boolean);
    const longest = prefixes
      .filter((p) => current === p || current.startsWith(p.endsWith('/') ? p : `${p}/`))
      .reduce((best, p) => Math.max(best, p.length), 0);
    if (longest) return 1 + longest;
    return current.split('/')[1] === item.path.split('/')[1] ? 1 : 0;
  };

  const handleLinkClick = (e, targetPath) => {
    // Already there: nothing to navigate to (it used to pin the sidebar open instead).
    if (location.pathname === targetPath) e.preventDefault();
  };

  const isOpen = !dismissed && (isHovering || openedByClick || showUserMenu);
  const effectivelyCollapsed = !isOpen;
  const foldNow = () => {
    setDismissed(true);
    setOpenedByClick(false);
    setShowUserMenu(false);
  };

  const renderIcon = (iconName, isItemActive) => {
    const IconComponent = ICON_MAP[iconName];
    if (!IconComponent) return null;
    return (
      <IconComponent
        size={18}
        className={`flex-shrink-0 ${isItemActive ? "text-white" : "text-slate-600"}`}
      />
    );
  };

  // Only one category is active (highlighted and forced open) at a time.
  const activeCategoryId = finalMenuItems.reduce((best, item) => {
    if (item.type === 'separator') return best;
    const score = activeScore(item);
    return score > best.score ? { id: item.id, score } : best;
  }, { id: null, score: 0 }).id;

  const toggleSubmenu = (itemId) => {
    setOpenMenus(prev => prev[itemId] ? {} : { [itemId]: true });
  };

  const renderMenuItem = (item) => {
    // Handle separator
    if (item.type === 'separator') {
      if (effectivelyCollapsed) return null;
      return (
        <div key={item.id} className="pt-4 pb-2">
          <span className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {item.label}
          </span>
        </div>
      );
    }

    const itemIsActive = item.id === activeCategoryId;
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuOpen = openMenus[item.id] || itemIsActive;

    // Filter submenu items
    const filteredSubmenu = item.submenu?.filter(subItem => {
      // Check vestasOnly
      if (subItem.vestasOnly && user?.company_name !== 'Vestas') return false;
      // Check invoiceOnly - only for users with invoice flag
      if (subItem.invoiceOnly && !user?.invoice) return false;
      // Check tsEnableDraftsOnly - only when company has v2 drafts enabled
      if (subItem.tsEnableDraftsOnly && !user?.tsEnableDrafts) return false;
      // Check carOrdersOnly - the Car Orders queue is opt-in per company
      // (Company.car_order_enabled, toggled in Django admin)
      if (subItem.carOrdersOnly && !user?.carOrderEnabled) return false;
      if (subItem.worktimeOnly && !user?.worktimeEnabled) return false;
      // Check excludeIfCoordinator - wyklucz dla coordinatorów (pokazujemy im wersję adminową)
      if (subItem.excludeIfCoordinator && user?.isCoordinator) return false;

      // Dla adminów filtruj submenu na podstawie viewMode
      if (isAdmin) {
        if (viewMode === 'admin') {
          // W trybie admin ukryj pozycje "my-*" w submenu
          if (subItem.id.startsWith('my-')) return false;
          // Sprawdź role tylko jeśli są zdefiniowane
          if (subItem.roles && subItem.roles.length > 0) {
            if (!subItem.roles.some(r => ['admin', 'adminves', 'superuser'].includes(r))) {
              // Chyba że ma allowCoordinator (wtedy to pozycja admina)
              if (!subItem.allowCoordinator) return false;
            }
          }
        } else {
          // W trybie user pokazuj tylko pozycje z rolami usera lub "my-*"
          const hasUserRole = !subItem.roles || subItem.roles.length === 0 || subItem.roles.some(r => ['user', 'userves'].includes(r));
          const isUserItem = subItem.id.startsWith('my-');
          if (!hasUserRole && !isUserItem) return false;
        }
      } else {
        // Standardowa logika dla nie-adminów
        if (subItem.roles && subItem.roles.length > 0 && !subItem.roles.includes(user?.role)) {
          // Check coordinator access for submenu
          const byCoordinator = subItem.allowCoordinator && user?.isCoordinator;
          // A company role that NAMES this page is a GRANT, not only a filter. Without this a
          // coordinator could be given the Resource Board in their role and still not see it:
          // the entry lists admin roles, the coordinator's own role is 'user', and the flag on
          // the entry is the only other way in — which would hand the page to every
          // coordinator in every company instead of the ones an admin picked.
          const byCompanyRole = hasMenuPermissions && (user.menuPermissions || []).includes(subItem.id);
          if (!byCoordinator && !byCompanyRole) return false;
        }
      }

      // Check submenu subscriptions
      if (subItem.requiredSubscriptions && subItem.requiredSubscriptions.length > 0) {
        return subItem.requiredSubscriptions.some(subType => hasSubscription(subType));
      }
      return true;
    });

    // Resolve category link to first visible subcategory path
    const resolvedPath = (filteredSubmenu && filteredSubmenu.length > 0)
      ? filteredSubmenu[0].path
      : item.path;

    return (
      <div key={item.id}>
        <div className="flex items-center">
          <Link
            to={resolvedPath}
            onClick={(e) => {
              handleLinkClick(e, resolvedPath);
              if (hasSubmenu && !openMenus[item.id]) {
                setOpenMenus({ [item.id]: true });
              }
            }}
            className={`flex-1 flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
              itemIsActive
                ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            } ${effectivelyCollapsed ? "justify-center" : ""}`}
            title={effectivelyCollapsed ? item.label : undefined}
          >
            {renderIcon(item.icon, itemIsActive)}
            {!effectivelyCollapsed && (
              <span className="text-sm font-bold truncate">{item.label}</span>
            )}
          </Link>
          {hasSubmenu && filteredSubmenu?.length > 0 && !effectivelyCollapsed && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSubmenu(item.id);
              }}
              className="p-2 text-slate-400 hover:text-slate-600"
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${isSubmenuOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
        {/* Submenu */}
        {hasSubmenu && filteredSubmenu?.length > 0 && !effectivelyCollapsed && isSubmenuOpen && (
          <div className="pl-6 py-1 space-y-0.5">
            {filteredSubmenu.map(subItem => (
              <Link
                key={subItem.id}
                to={subItem.path}
                className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  location.pathname === subItem.path
                    ? "text-brand-600 font-bold bg-brand-50"
                    : "text-slate-600 hover:text-brand-600 hover:bg-slate-50"
                }`}
              >
                <span className="flex-1">{subItem.label}</span>
                {subItem.id === 'cars-issues' && openIssueCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                    {openIssueCount > 99 ? '99+' : openIssueCount}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getRoleLabel = () => {
    const roles = [];
    if (isAdmin) {
      if (viewMode === 'user') roles.push('User View');
      else if (user?.role === 'superuser') roles.push('Superuser');
      else roles.push('Admin');
    }
    // Coordinator only if not admin
    if (!isAdmin && user?.isCoordinator) roles.push('Coordinator');
    // Inspector always if applicable
    if (user?.inspector) roles.push('Inspector');
    return roles.length > 0 ? roles.join(' / ') : 'User';
  };

  return (
    // The outer box holds the rail's 64px in the page's layout for good; the panel inside
    // widens on top of the page, so the page never reflows under the pointer.
    <div className="relative w-16 flex-shrink-0 h-screen" data-app-sidebar-rail>
    <div
      ref={sidebarRef}
      data-app-sidebar
      data-open={isOpen ? 'true' : 'false'}
      className={`absolute inset-y-0 left-0 z-40 bg-white border-r border-slate-200 flex flex-col h-screen transition-[width] duration-200 overflow-x-hidden ${
        effectivelyCollapsed ? "w-16" : "w-64 shadow-xl"
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => { setIsHovering(false); setDismissed(false); }}
      onClick={() => { if (!isOpen) { setDismissed(false); setOpenedByClick(true); } }}
    >
      {/* Logo Header */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-slate-100 overflow-hidden">
        {!effectivelyCollapsed ? (
          <div className="flex items-center gap-2">
            <img src={logoSygnet} alt="Entrigy" className="w-8 h-8 object-contain flex-shrink-0" />
            <span className="text-brand-500 text-lg tracking-widest whitespace-nowrap" style={{ fontFamily: 'Arial Black, sans-serif', fontWeight: 900 }}>ENTRIGY</span>
          </div>
        ) : (
          <img src={logoSygnet} alt="Logo" className="w-8 h-8 object-contain mx-auto" />
        )}
        {!effectivelyCollapsed && (
          <button
            data-app-sidebar-fold
            title="Fold the menu"
            onClick={(e) => { e.stopPropagation(); foldNow(); }}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className={`h-4 w-4 transition-transform ${effectivelyCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
        {subscriptionLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
            {!effectivelyCollapsed && <span className="ml-2 text-sm text-slate-600">Loading...</span>}
          </div>
        ) : (
          finalMenuItems.map(item => renderMenuItem(item))
        )}
      </nav>

      {/* Footer */}
      <div className="p-2 mt-auto border-t border-slate-100">
        {/* Phone Book Button */}
        {onOpenPhoneBook && !effectivelyCollapsed && (
          <button
            onClick={onOpenPhoneBook}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors mb-1"
          >
            <Phone size={18} className="flex-shrink-0" />
            <span className="text-sm font-bold">Phone Book</span>
          </button>
        )}

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
            }}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 w-full transition-all ${
              effectivelyCollapsed ? "justify-center" : ""
            }`}
          >
            <div className="relative">
              {user?.profile_picture || user?.profilePicture ? (
                <img src={user.profile_picture || user.profilePicture} alt="Profile" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-black text-brand-600">
                    {user?.firstName ? `${user.firstName.charAt(0)}${user.lastName?.charAt(0) || ''}` : 'U'}
                  </span>
                </div>
              )}
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </div>
            {!effectivelyCollapsed && (
              <div className="text-left flex-1 min-w-0">
                <span className="text-sm font-bold text-slate-900 block truncate">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Profile"}
                </span>
                <span className="text-[10px] font-bold text-brand-500 uppercase">{getRoleLabel()}</span>
              </div>
            )}
          </button>

          {showUserMenu && (
            <div className={`absolute ${effectivelyCollapsed ? "left-16" : "left-0"} bottom-full mb-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden`}>
              {/* Company header — logo + name so the user always knows which company they're in */}
              {(companyInfo?.company_logo || companyInfo?.name || user?.companyName) && (
                <div className="px-3 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
                  {companyInfo?.company_logo ? (
                    <img src={companyInfo.company_logo} alt="Company" className="h-7 max-w-[120px] object-contain" />
                  ) : (
                    <span className="text-sm font-black text-slate-700 truncate">{companyInfo?.name || user?.companyName}</span>
                  )}
                </div>
              )}
              {/* Plan v2 E3: private-account sessions switch between the person's accounts here */}
              <AccountSwitcher onSwitched={() => setShowUserMenu(false)} />
              {/* User info header */}
              <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                {user?.profile_picture || user?.profilePicture ? (
                  <img src={user.profile_picture || user.profilePicture} alt="Profile" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-black text-brand-600">{user?.firstName ? `${user.firstName.charAt(0)}${user.lastName?.charAt(0) || ''}` : 'U'}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">{user?.firstName} {user?.lastName}</p>
                  {/* The logged-in person's own address — NOT the company PPE inbox */}
                  {user?.email && (
                    <p className="text-[10px] text-slate-500 truncate" title={user.email}>{user.email}</p>
                  )}
                </div>
              </div>

              {/* Notifications */}
              {notificationCount > 0 && (
                <div className="border-b border-slate-100">
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notifications</span>
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{notificationCount}</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {ticketNotifications.slice(0, 3).map((notification) => (
                      <div
                        key={notification.ticket_id}
                        onClick={() => {
                          navigate('/settings', { state: { activeTab: 'support', selectedTicketId: notification.ticket_id } });
                          setShowUserMenu(false);
                        }}
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <MessageCircle className="h-4 w-4 text-brand-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate">{notification.subject}</p>
                            <p className="text-[10px] text-slate-400">{new Date(notification.response_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Menu items.

                  ONE list, whatever the role. These entries used to be split in two:
                  an admin got Personal + Company Settings, a user additionally got
                  My Profile and two Powerpass entries — so the same person switching
                  view mode saw a different menu leading to the same screens. An admin
                  has a profile, a Powerpass and personal settings exactly like anyone
                  else; the only genuinely role-bound entry here is Company Settings,
                  which keeps its own gate below. */}
              <div className="p-1" data-user-menu>
                <Link
                  to="/my-profile"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <User size={14} className="text-brand-500" />
                  My Profile
                </Link>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => {
                    // Open the badge at its shareable address so what the user
                    // sees in the URL bar is the same link the QR encodes.
                    navigate(user?.public_profile_token
                      ? `/p/${user.public_profile_token}`
                      : `/powerpass/${user?.id}`);
                    setShowUserMenu(false);
                  }}
                >
                  <CreditCard size={14} className="text-purple-500" />
                  My Powerpass
                </button>
                {/* Personal — Powerpass, Work Experience i Settings (hasło, podpis,
                    sidebar, PPE, Resource Board) dla wszystkich.
                    "View Powerpass" nie ma tu już własnej pozycji: to zakładka w
                    Personal, a dwa wejścia do tego samego modala z jednego menu
                    tylko wydłużały listę. */}
                <Link
                  to="/profile"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <Settings size={14} />
                  Personal
                </Link>
                {/* Company Settings - admin, którego rola ma Company Settings (admin bez roli: zawsze);
                    sam inspektor - tylko zakładka PPE */}
                {((isAdmin && user?.canChangeCompanySettings !== false) || user?.inspector) && (
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings size={14} />
                    Company Settings
                  </Link>
                )}
                <Link
                  to="/support"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <MessageCircle size={14} className="text-blue-500" />
                  Support
                </Link>
                <Link
                  to="/changes"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <FileText size={14} />
                  Changes List
                </Link>
                {/* Switch View Mode - tylko dla adminów */}
                {isAdmin && (
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    onClick={() => {
                      setViewMode(viewMode === 'admin' ? 'user' : 'admin');
                      setShowUserMenu(false);
                      navigate('/dashboard');
                    }}
                  >
                    <ArrowLeftRight size={14} />
                    Switch to {viewMode === 'admin' ? 'User' : 'Admin'} View
                  </button>
                )}
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  onClick={handleLogout}
                >
                  <LogOut size={14} />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default UnifiedSidebar;
